import { useState } from "react";
import { toast } from "sonner";

import { NuevoCasoDialog } from "@/components/laboratorio/NuevoCasoDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Skeleton } from "@/components/ui/skeleton";
import { useLlamada } from "@/hooks/useLaboratorio";
import { formatearCostoUsd, formatearTokens } from "@/lib/formatoDeConsumo";
import { estadoDeLaLlamada, formatearDuracion, type DetalleDeLlamada as Detalle, type PayloadDeLlamada } from "@/lib/laboratorio";
import { cn } from "@/lib/utils";

/** El detalle de una llamada al modelo y el atajo para llevar su informe al laboratorio como caso. */
export function DetalleDeLlamada({
  id,
  corridaId,
  onCasoCreado,
}: {
  id: number;
  corridaId: number | null;
  onCasoCreado: () => void;
}) {
  const llamada = useLlamada(id);
  const [creando, setCreando] = useState(false);

  if (llamada.isPending) return <Skeleton className="h-96 rounded-[1.25rem]" />;
  if (llamada.isError) return <p className="text-meta text-danger-texto">No pudimos cargar la llamada.</p>;

  const l = llamada.data;
  const estado = estadoDeLaLlamada(l.estado);
  const informe = l.corrida.informe;
  const camino = [informe?.numero, l.corrida.motivo].filter(Boolean).join(" · ") + (l.corrida.paso ? ` / ${l.corrida.paso}` : "");
  const nombre = l.funcion?.nombre ?? "Llamada al modelo";

  return (
    <Card variant="marcada" className="gap-3.5 rounded-[1.25rem] px-[1.375rem] py-[1.125rem]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="rotulo-seccion">{camino}</p>
          <div className="mt-0.5 flex items-center gap-2.5">
            <h3 className="text-ink text-[1.0625rem] font-extrabold tracking-[-0.02em]">{nombre}</h3>
            <Etiqueta tono={estado.tono} className="rounded-[0.4375rem] px-2 text-[0.6875rem]">
              {estado.etiqueta}
            </Etiqueta>
          </div>
        </div>
        {informe && corridaId !== null ? (
          <Button type="button" variant="secondary" className="rounded-[0.75rem] text-[0.78125rem] font-bold" onClick={() => setCreando(true)}>
            Llevar al laboratorio como caso
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Dato etiqueta="Prompt" valor={l.funcion ? `${l.funcion.nombre}${l.revision ? ` · ${l.revision.etiqueta}` : ""}` : "—"} />
        <Dato etiqueta="Modelo" valor={l.modelo ?? "—"} />
        <Dato etiqueta="Entrada" valor={l.tokens_entrada === null ? "—" : `${formatearTokens(l.tokens_entrada)} tokens`} />
        <Dato etiqueta="Salida" valor={l.tokens_salida === null ? "—" : `${formatearTokens(l.tokens_salida)} tokens`} />
        <Dato etiqueta="Costo" valor={formatearCostoUsd(l.costo_usd)} />
        <Dato etiqueta="Tiempo" valor={formatearDuracion(l.latencia_ms)} />
      </div>

      {l.error ? (
        <p className="text-danger-texto text-[0.78125rem] font-bold">
          <span className="rotulo-seccion text-danger-texto mr-2">Tipo de error</span>
          <span>{l.error}</span>
        </p>
      ) : null}

      {l.herramientas.length > 0 ? <Herramientas herramientas={l.herramientas} /> : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <Bloque titulo="Entrada" payload={l.entrada} />
        <Bloque titulo="Salida" payload={l.salida} error={l.estado === "err"} />
      </div>

      <NuevoCasoDialog
        origen={creando && corridaId !== null ? { corridaId, nombreSugerido: [informe?.numero, l.corrida.motivo].filter(Boolean).join(" · ") } : null}
        onCerrar={() => setCreando(false)}
        onCreado={() => {
          toast.success("Caso agregado al laboratorio.");
          onCasoCreado();
        }}
      />
    </Card>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="border-border rounded-xl border-[1.5px] px-2.5 py-2">
      <p className="text-ink-medio text-[0.625rem] font-extrabold tracking-[0.06em] uppercase">{etiqueta}</p>
      <p className="mt-0.5 font-mono text-[0.75rem] font-bold">{valor}</p>
    </div>
  );
}

function Herramientas({ herramientas }: { herramientas: Detalle["herramientas"] }) {
  const texto = (valor: unknown) => (typeof valor === "string" ? valor : JSON.stringify(valor));

  return (
    <section aria-label="Herramientas que usó el agente" className="flex flex-col gap-1.5">
      <p className="rotulo-seccion">Herramientas que usó el agente</p>
      {herramientas.map((h, i) => (
        <p key={i} className="bg-superficie-tenue rounded-[0.5625rem] px-2.5 py-1.5 font-mono text-[0.71875rem] font-semibold break-words">
          <span className="text-success-soft-foreground">
            {h.nombre ?? "herramienta"}({texto(h.argumentos)})
          </span>{" "}
          <span className="text-ink-medio">→ {h.resultado === null || h.resultado === undefined ? "sin resultado" : texto(h.resultado)}</span>
        </p>
      ))}
    </section>
  );
}

function Bloque({ titulo, payload, error }: { titulo: string; payload: PayloadDeLlamada | null; error?: boolean }) {
  return (
    <section aria-label={titulo} className="flex min-w-0 flex-col gap-1.5">
      <p className="rotulo-seccion">{titulo}</p>
      <pre
        className={cn(
          "border-border max-h-[18.75rem] overflow-auto rounded-xl border-[1.5px] px-3.5 py-3 font-mono text-[0.71875rem] leading-[1.6] whitespace-pre-wrap",
          error ? "bg-danger-soft text-danger-texto-fuerte" : "bg-superficie-tenue",
        )}
      >
        {payload ? payload.texto : "Sin registro."}
      </pre>
      {payload?.truncado ? <p className="text-ink-medio text-[0.6875rem] font-semibold">Se muestran solo los primeros 30.000 caracteres.</p> : null}
    </section>
  );
}
